import React, {Fragment, useContext} from "react";
import {View, Text, Image} from "@react-pdf/renderer";
import PropTypes from "prop-types";

import {
  detectComplexLaborCartItem,
  getPriceWithCurrency,
  getTotalPrice,
  toFixedFeatureCost,
  toFixedPrice
} from "../../../misc/helpers";
import {PrintDocumentContext} from "./printDocumentContext";
import {getCartItemName, getFeatureUnit} from "./printDocumentFunctions";
import {cartItemInterface} from "../../shoppingCart/cartItemInterface";
import {getPriceWithIncluded, getShowPrice} from "../../../misc/priceFunctions";
import PrintDocumentBodyBundledCartItems from "./printDocumentBodyBundledCartItems";
import {SQFT_FEATURES} from "../../sqft/constants";

const PrintDocumentBodyCartItem = (props) => {
  const {styles, cartItem, fabricatorData, locale, cartItems, sqftSlabState, cartItemsThumbnails} = props;

  const fabricatorSettings = fabricatorData.settings;
  const isComplexMultipliedUsed = detectComplexLaborCartItem(cartItem) && getTotalPrice().multipliedCostUsed;
  //const shouldUseSlabPricing = cartItem.shouldUseSlabPricing; // calculate shouldUseSlabPricing per cItem:
  const shouldUseSlabPricing = cartItem.partId === SQFT_FEATURES.slabs && sqftSlabState.isSlabPricingEnabled; // calculate shouldUseSlabPricing globally
  const priceTotal = isComplexMultipliedUsed || cartItem.displayFakePrice === 0
    ? null
    : toFixedFeatureCost(cartItem.cartFeature, cartItem.quantity, cartItem.fromQtCategory, true, cartItem, shouldUseSlabPricing, cartItem.currentQuote);
  const printContext = useContext(PrintDocumentContext);

  const getFeatureQuantity = () => {
    return printContext.messages["receipt.content.featureQuantity"] || ""
  };

  const getPrice = () => {
    const showPrice = getShowPrice(fabricatorData);
    if (!showPrice) {
      return null
    }
    return getPriceWithIncluded(priceTotal, cartItem.cartFeature, fabricatorData, printContext.messages)
  };

  const featurePrice = toFixedFeatureCost(cartItem.cartFeature, 1, cartItem.fromQtCategory, false, null, shouldUseSlabPricing, cartItem.currentQuote);
  const price = getPrice();
  const hideUnitPrice = (cartItem.partId === "room" && !featurePrice) || !price;
  const isMinimumCharge = cartItem.isMinimumCharge && !cartItem.isMinimumChargeItems;
  const displayFake = cartItem.displayFakeQuantity && (typeof cartItem.displayFakeQuantity === "number") && (cartItem.displayFakeQuantity > 0);

  const bundleProducts = cartItems ? cartItems.filter(cItem => {
    return cItem.bundleParentId && (cItem.bundleParentId === cartItem.id)
  }) : [];

  const withThumbnail = fabricatorData.settings.showCartItemThumbnailsInReceipt && cartItemsThumbnails
    && cartItemsThumbnails[cartItem.id];

  if (withThumbnail) {
    return (
      <View wrap={true}>
        <View style={[styles.cartItem, styles.cartItemWithThumbnail]} wrap={false}>
          <View style={styles.cartItemThumbnail} wrap={false}>
            <Image style={styles.cartItemThumbnailImage} wrap={false} source={{
              uri: cartItemsThumbnails[cartItem.id], method: "GET", headers: { "Cache-Control": "no-cache" }
            }} />
          </View>
          <View style={styles.cartItemWithThumbnailBody} wrap={false}>
            <Text style={isMinimumCharge ? [styles.cartItemName, styles.cartItemNameCentered] : [styles.cartItemName, styles.cartItemNameBig]}>
              {getCartItemName(cartItem.cartFeature, fabricatorData, printContext, locale)}
            </Text>
            {cartItem.cartFeature.dbFeature.sku && !fabricatorSettings.hideSku ? (
              <Text style={styles.cartItemSkuFullWidth}>
                {`${printContext.messages["receipt.content.featureSKU"]}: ${cartItem.cartFeature.dbFeature.sku}`}
              </Text>
            ) : null}
            {cartItem.cartFeature.dbFeature.description ? (
              <Text style={[styles.wizardItemDescription, styles.cartItemDescription]}>
                {cartItem.cartFeature.dbFeature.description}
              </Text>
            ) : null}
            <View style={styles.cartItemInfo} wrap={false}>
              {isMinimumCharge || fabricatorData.hideCheckoutBreakdownCost
                ? null
                : (
                  <Text style={styles.cartItemUnit}>
                    {`${getFeatureQuantity()}: ${toFixedPrice(displayFake ? cartItem.displayFakeQuantity : cartItem.quantity)}`}
                  </Text>
                )}
              <Text style={styles.cartItemUnitItem}>
                {hideUnitPrice || isMinimumCharge
                  ? null
                  : `${getFeatureUnit(cartItem.cartFeature, fabricatorData, printContext, locale, cartItem.quantity)}: ${getPriceWithCurrency(fabricatorSettings, featurePrice, cartItem.cartFeature)}`}
              </Text>
              <Text style={styles.cartItemPrice}>{price}</Text>
            </View>
            {cartItem.noteText ? <Text wrap={false} style={styles.cartItemNote}>{cartItem.noteText}</Text> : null}
          </View>
        </View>
        {bundleProducts.length ? <PrintDocumentBodyBundledCartItems locale={locale}
                                                                    cartItems={cartItems}
                                                                    cartItem={cartItem}
                                                                    fabricatorData={fabricatorData}
                                                                    styles={styles}/> : null}
      </View>
    )
  }

  return (
    <Fragment>
      <View style={styles.cartItem} wrap={true}>
        <View wrap={false}>
          <Text style={isMinimumCharge ? [styles.cartItemName, styles.cartItemNameCentered] : styles.cartItemName}>
            {getCartItemName(cartItem.cartFeature, fabricatorData, printContext, locale)}
          </Text>
          <View style={styles.cartItemInfo}>
            {cartItem.cartFeature.dbFeature.sku && !fabricatorSettings.hideSku ? (
              <Text style={[styles.cartItemUnit, styles.cartItemUnitSKU]}>
                {`${printContext.messages["receipt.content.featureSKU"]}: ${cartItem.cartFeature.dbFeature.sku}`}
              </Text>
            ) : null}
            {isMinimumCharge || fabricatorData.hideCheckoutBreakdownCost
              ? null
              : (
                <Text style={styles.cartItemUnit}>
                  {`${getFeatureQuantity()}: ${toFixedPrice(displayFake ? cartItem.displayFakeQuantity : cartItem.quantity)}`}
                </Text>
              )}
            <Text style={styles.cartItemUnitItem}>
              {hideUnitPrice || isMinimumCharge
                ? null
                : `${getFeatureUnit(cartItem.cartFeature, fabricatorData, printContext, locale, cartItem.quantity)}: ${getPriceWithCurrency(fabricatorSettings, featurePrice, cartItem.cartFeature)}`}
            </Text>
            <Text style={styles.cartItemPrice}>{price}</Text>
          </View>
          {cartItem.noteText ? <Text style={styles.cartItemNote}>{cartItem.noteText}</Text> : null}
        </View>
      </View>
      {bundleProducts.length ? <PrintDocumentBodyBundledCartItems locale={locale}
                                                                  cartItems={cartItems}
                                                                  cartItem={cartItem}
                                                                  fabricatorData={fabricatorData}
                                                                  styles={styles}/> : null}
    </Fragment>

  )
};

PrintDocumentBodyCartItem.propTypes = {
  cartItem: PropTypes.shape(cartItemInterface),
  fabricatorData: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
};

export default PrintDocumentBodyCartItem

